import clsx from 'clsx';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import { FadeIn } from 'components/common/FadeIn';
import DrawCounter from 'components/home-page/DrawCounter';
import { EnterButton } from 'components/home-page/enter-button/EnterButton';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import Trustpilot from '../../svg/Trustpilot/trustpilot-white.svg';
import DesktopWave from '../../svg/Waves/homepage-hero-wave-desktop.svg';
import MobileWave from '../../svg/Waves/homepage-hero-wave-mobile.svg';
import styles from './hero.module.css';

interface HeroProps {
  nextDrawDate: string;
  withCountdown?: boolean;
}

const Hero = observer(({ withCountdown, nextDrawDate }: HeroProps) => {
  const { isMobile } = useIsMobile();
  const {
    subscriptionStatusInfo,
    subscriptionsInitialized,
    currentUser,
    userInitialized,
  } = useUserStore();
  const showEnterButton =
    (userInitialized && !currentUser) ||
    (subscriptionsInitialized && !subscriptionStatusInfo.subscriberStatus);

  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <div className={styles.hero}>
        <div
          className={styles.heroImage}
          style={{
            transform: `translateY(${scrollY * 0.3}px)`,
          }}
        >
          <div className={styles.heroImageGradient} />
        </div>
        <div className={styles.heroContent}>
          <AboutUsSiteContainer styles2026>
            <FadeIn>
              <div className={styles.heroTrustpilot}>
                <Trustpilot />
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className={clsx('daymadeTypographyXXL', styles.heroTitle)}>
                Win prizes that <br />
                <span className={styles.heroTitleTeal}>make your day</span>
              </div>
            </FadeIn>
            <FadeIn delay={0.3}>
              <div
                className={clsx(
                  'daymadeTypographyMMedium',
                  styles.heroSubtitle,
                )}
              >
                Dream holidays, home renovations {isMobile && <br />}& so much
                more
              </div>
            </FadeIn>
            <div className={styles.ctaCountdownWrapper}>
              {withCountdown && (
                <FadeIn delay={0.3}>
                  <div className={styles.heroCountdown}>
                    <div className="daymadeTypographySBold">Next draw in</div>
                    <DrawCounter
                      targetDate={new Date(nextDrawDate)}
                      introText=""
                      edition2026
                    />
                  </div>
                </FadeIn>
              )}
              {showEnterButton && (
                <FadeIn delay={0.3}>
                  <div className={styles.heroCTA}>
                    <EnterButton
                      dm2026Button
                      buttonFullWidth
                      buttonSize="Large"
                    />
                  </div>
                </FadeIn>
              )}
              {(withCountdown || showEnterButton) && (
                <FadeIn delay={0.3}>
                  <div
                    className={clsx(
                      showEnterButton && styles.subCTA,
                      'daymadeTypographySRegular',
                      'textWhite',
                    )}
                  >
                    Join 100k+ weekly players
                  </div>
                </FadeIn>
              )}
            </div>
          </AboutUsSiteContainer>
        </div>
        <div className={styles.wave}>
          <div className={styles.waveWrapper}>
            {isMobile ? <MobileWave /> : <DesktopWave />}
          </div>
        </div>
      </div>
    </>
  );
});

export default Hero;
